import { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";

import API from "api/api";
import { deleteCEmail } from "api/cemail";

import { AppContext } from "context/AppContext";
import Typography from "common/Typography/Typography";
import BaseModal from "common/Modal/BaseModal";
import Message from "common/Message/Message";

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled(Typography)`
    margin: ${Spacing[16]} 0 ${Spacing[24]};
    // tablet and mobile
    @media (max-width: ${MediaQueries.lg}) {
        margin: ${Spacing[8]} 0;
    }
`;

const CancelButton = styled(Button)`
    width: 108px;
`;

const DeleteButton = styled(Button)`
    width: 108px;
`;

const DeleteModal = ({ closeModal, factorType, subType, defaultValue, factorId }) => {
    const { t } = useTranslation(["deleteModal"]);
    const { fetchProfileData, dispatch, setCEmails } = useContext(AppContext);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const deleteAdditionalEmail = async () => {
        const res = await deleteCEmail({
            emailAddress: defaultValue
        });
        if (res.succeed) {
            setCEmails((prev) => {
                return prev.filter((item) => item.emailAddr !== defaultValue);
            });
        }
        return res;
    };

    const deleteSecondaryEmail = async () => {
        const emptyEmail = "";
        const res = await API.updateProfile({
            type: "secondaryEmail",
            emailAddress: emptyEmail,
            factorId: ""
        });
        if (res.succeed) {
            dispatch({
                type: "CONTACT",
                payload: {
                    secondaryEmail: emptyEmail
                }
            });
        }
        return res;
    };

    const deleteNumber = async () => {
        const res = await API.deleteFactor({
            factorType,
            factorValue: defaultValue,
            factorId: factorId
        });

        if (res.succeed) {
            dispatch({
                type: "FACTOR",
                payload: {
                    [factorType]: {}
                }
            });
        }
        return res;
    };

    const handleDelete = async () => {
        setLoading(true);
        let response;
        if (subType === "additional") {
            response = await deleteAdditionalEmail();
        } else if (subType === "secondary") {
            response = await deleteSecondaryEmail(factorId);
        } else {
            response = await deleteNumber();
        }

        setLoading(false);
        if (response.succeed) {
            fetchProfileData();
            closeModal();
        } else {
            setError(response.message);
        }
    };

    const handleClose = () => {
        setError("");
        closeModal();
    };

    useEffect(() => {
        let timer;
        if (error !== "") {
            timer = setTimeout(() => setError(""), 3000);
        }
        return () => clearTimeout(timer);
    }, [error]);

    const tKey = `${factorType}${subType ? `.${subType}` : ""}`;

    return (
        <BaseModal
            size="default"
            secondaryButton={
                <CancelButton
                    variant="secondaryNeutral"
                    onClick={handleClose}
                    data-track="analytics"
                    data-bname="close-delete-modal"
                >
                    {t("deleteModal:button.secondary")}
                </CancelButton>
            }
            primaryButton={
                <DeleteButton
                    variant="destructive"
                    onClick={handleDelete}
                    data-track="analytics"
                    data-bname="confirm-delete"
                >
                    {t("deleteModal:button.primary")}
                </DeleteButton>
            }
            closeModal={handleClose}
            loading={loading}
        >
            <Content>
                <Title variant="h2" display="block" align="center">
                    {t(`deleteModal:${tKey}.title`)}
                </Title>
                <Typography variant="body" align="center" display="block">
                    {t(`deleteModal:${tKey}.subtitle`, { value: defaultValue })}
                </Typography>
                {error !== "" && (
                    <Message type="error" message={error} align="left" />
                )}
            </Content>
        </BaseModal>
    );
};

export default DeleteModal;

DeleteModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    factorType: PropTypes.oneOf(["email", "call", "sms"]).isRequired,
    subType: PropTypes.oneOf(["additional", "secondary"]),
    defaultValue: PropTypes.string.isRequired,
    factorId: PropTypes.string.isRequired
};

DeleteModal.defaultProps = {
    subType: undefined
};
